import { AuthContext, isMobileDevice } from '@repo-level/core-front';
import { LocalizedLink } from 'components/LocalizedLink';
import { resetCachePayments } from 'helpers/payments';
import { useHeaderMenuList } from 'hooks/headerMenuList';
import { useTheme } from 'hooks/theme';
import { useMedia } from 'modules/common/hooks/useMedia';
import type { FC } from 'react';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentRoute } from 'react-navi';
import { ScMenuDesktop, ScNavIndicator } from './styled';

interface MenuDesktopProps {
  onClick?: () => void;
}

export const MenuDesktop: FC<MenuDesktopProps> = ({ onClick }) => {
  const { t } = useTranslation();
  const { menu } = useTheme();

  const {
    url: { href: currentHref },
  } = useCurrentRoute();

  const { isAuthenticated, resetUser } = useContext(AuthContext);
  const { isMobile } = useMedia();

  const [activeItemOffsetTop, setActiveItemOffsetTop] = useState(0);

  const handleClose = async () => {
    await resetUser();
    await resetCachePayments();
    onClick?.();
  };

  const menuList = useHeaderMenuList();

  const checkItemActive = useCallback(
    (url) =>
      currentHref === url ||
      currentHref.includes(`${url}/`) ||
      currentHref.includes(`${url}?`) ||
      currentHref.includes(`${url}#`),
    [currentHref]
  );

  const noActiveMenuItem = useMemo(
    () => !menuList.some(({ url }) => checkItemActive(url)),
    [menuList, checkItemActive]
  );

  const handleLogoutClick = () => {
    handleClose();
  };

  const handleMenuItemClick = () => {
    onClick?.();
  };

  return (
    <ScMenuDesktop isMobile={isMobileDevice}>
      {!isMobile && activeItemOffsetTop && !noActiveMenuItem ? (
        <ScNavIndicator top={activeItemOffsetTop} />
      ) : null}

      {menuList.map(
        (
          { name, url, icon, iconSettings, badge, isHidden, sendEvent },
          index
        ) => {
          const activeItem = checkItemActive(url);

          return (
            !isHidden && (
              <li
                className={activeItem ? 'active' : ''}
                key={index}
                ref={(node) => {
                  if (activeItem && node) {
                    const newOffset =
                      node.offsetTop + node.getBoundingClientRect().height / 2;
                    setActiveItemOffsetTop((prev) =>
                      prev === newOffset ? prev : newOffset
                    );
                  }
                }}
              >
                <LocalizedLink
                  href={url}
                  onClick={() => {
                    sendEvent && sendEvent();
                    handleMenuItemClick();
                  }}
                >
                  <div
                    className={
                      iconSettings?.isStroke ? 'stroke-icon' : 'fill-icon'
                    }
                  >
                    {icon}
                  </div>
                  <div className="name">{name}</div>
                  {badge}
                </LocalizedLink>
              </li>
            )
          );
        }
      )}
      <li>
        <button id="intercomcustomLauncher" type="button">
          <div className="fill-icon">
            <menu.helpdeskIcon.component />
          </div>

          <div className="name">{t('glossary:helpdesk')}</div>
        </button>
      </li>
      {isAuthenticated && (
        <li>
          <button onClick={handleLogoutClick} type="button">
            <div className="fill-icon">
              <menu.signOutIcon.component />
            </div>

            <div className="name">{t('glossary:signOut')}</div>
          </button>
        </li>
      )}
    </ScMenuDesktop>
  );
};
